import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, UserPlus, Shield } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface Admin {
  user_id: string;
  email?: string;
  created_at: string;
}

const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      // Get all admin user IDs
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('*');

      if (adminError) throw adminError;

      // Get user emails from auth.users using service function
      const { data: usersData, error: usersError } = await supabase.functions.invoke('get-admin-users');
      
      if (usersError) throw usersError;

      // Match admin IDs with user emails
      const adminsWithEmails = adminData?.map(admin => {
        const userInfo = usersData?.users?.find((u: any) => u.id === admin.user_id);
        return {
          ...admin,
          email: userInfo?.email || 'Unknown'
        };
      }) || [];

      setAdmins(adminsWithEmails);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin list",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    setIsAddingAdmin(true);
    try {
      // First, find the user by email using the admin function
      const { data: usersData, error: usersError } = await supabase.functions.invoke('get-admin-users');
      
      if (usersError) throw usersError;

      const user = usersData?.users?.find((u: any) => u.email === newAdminEmail.trim());
      
      if (!user) {
        toast({
          title: "Error",
          description: "User with this email not found. The user must first register an account.",
          variant: "destructive",
        });
        return;
      }

      // Check if already an admin
      const { data: existingAdmin } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', user.id)
        .single();

      if (existingAdmin) {
        toast({
          title: "Error",
          description: "This user is already an admin",
          variant: "destructive",
        });
        return;
      }

      // Add to admins table
      const { error: insertError } = await supabase
        .from('admins')
        .insert({ user_id: user.id });

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: `${newAdminEmail} has been added as an admin`,
      });

      setNewAdminEmail('');
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add admin. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const removeAdmin = async (userId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('admins')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${email} has been removed from admin role`,
      });

      fetchAdmins();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin Management</h1>
        <p className="text-muted-foreground">Manage admin users and permissions</p>
      </div>

      {/* Add New Admin */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New Admin
          </CardTitle>
          <CardDescription>
            Add a new admin user by email address. The user must already have an account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="adminEmail">Email Address</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder="Enter user email..."
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button 
              onClick={addAdmin} 
              disabled={isAddingAdmin}
              className="sm:mt-6"
            >
              {isAddingAdmin ? 'Adding...' : 'Add Admin'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Admins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Current Admins
          </CardTitle>
          <CardDescription>
            All users with admin privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4">Loading admins...</div>
          ) : admins.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">No admins found</div>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium">{admin.email}</div>
                    <div className="text-sm text-muted-foreground">
                      Added: {new Date(admin.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Admin</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove {admin.email} from admin role? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => removeAdmin(admin.user_id, admin.email)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove Admin
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminManagement;